import _ from "underscore";
import idSerializer from "geotab-uuid";
import DriveView from "../../DriveView";
import { htmlEscape, show, hide, empty, echo, isVisible } from "../../utils/vanilla";
import { Device } from "../../model/Device";
import { generateChecklist, ListView } from "../framework/checklist";
import { ICredentialsWithServer } from "../../utils/credentialsStore";
import { SuggestedVehicles } from "./suggestedVehicle";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { handleApi } from "../../utils/error";
import { BaseCache, makeDeviceCache } from "../../utils/storage/cache";
import * as logger from "../../utils/logger";
import { Dialog } from "../framework/dialog";
import { getCurrentVehicle, setCurrentVehicle, isNoVehicle } from "../../utils/device";
import Api from "../../services/api";
import Sync from "../../sync/sync";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { getServerDate } from "../../utils/storage/date";
import { getHomeHash } from "../../utils/uri";
import Spinner from "../../utils/spinner";
import ButtonsBar from "../framework/buttonsBar";
import { NetworkStates, PageLevel } from "../../store/mobile/types";
import { ActiveEventType, WorkdayType } from "../../store/data/types";
import DutyStatusLog from "../../model/DutyStatusLog";
import toastr from "../../utils/toastr";
import { isIoxUsbDeviceAttached } from "../../services/ioxDataHandler";
import { clearTempAssetsFromState, getAttachmentsFromState, setAttachmentsInState, IVehicleListItem } from "./helper";
import { searchVehicles } from "../../modules/assets/helpers/vehicleSearchHelper";
import { getLastDriverForDevice, needsDriverChangeConfirmation } from "../../modules/assets/helpers/vehicleSelectionHelper";
import { TrailerAttachment } from "../../model/TrailerAttachment";
import { TrailerAttachments } from "../../model/TrailerAttachments";

import pageHTML from "./selectVehicle.tmpl";
import { getDeviceStatusInfo } from "../../utils/helpers";
import { getLastPosition } from "../../utils/location";
import LatLon from "../../utils/latlon";
import { DataActions } from "../../store/data/actions";
import { OpenCabWorkflowActions } from "../../store/openCabWorkflow/actions";
import { SyncType } from "../../sync/sync.types";
import { getDeviceAssignedToDriver } from "../../sync/driverChangeHelper";
import PerformanceProfilerFactory from "../../utils/performanceProfiler/performanceProfilerFactory";
import { MeasureName } from "../../utils/performanceProfiler/measureName";
import { deferUntilIdle } from "../../utils/deferUntilIdle";
import { isVerboseLogging } from "../../utils/logger.internal";
import { syncTypesForWorkflow } from "../workflow";
const KEYUP_TIMEOUT_OPTIMIZED_MS = 250;

const VEHICLE_SELECTED_SYNC_TYPES = [SyncType.TrailerAttachments, SyncType.Shipments, SyncType.DVIR, SyncType.Media];

export default class SelectVehicleView extends DriveView<Backbone.Model> {

    protected strings = [
        "Selecting vehicle...",
        "Searching for '{0}'",
        "Synchronizing Asset Inspection logs, attached trailers, HOS, shipments, and messages...",
        "Could not find any vehicles matching your search terms.",
        "Please try again.",
        "Continue without vehicle",
        "Continue",
        "Select another vehicle",
        "Please wait",
        "You have been assigned to",
        "Could not find any recent or nearby vehicles.",
        "No nearby vehicles detected. Check your GPS settings or move to an open area for better signal.",
        "Searching for recent or nearby vehicles...",
        "Device could not be attached due to network issues.",
        "Synchronizing HOS"
    ];
    private readonly template = _.template(pageHTML);
    private readonly api: Api;
    private readonly sync: Sync;
    private readonly deviceCache: BaseCache<IDevice | Device>;
    private readonly user: DeepReadonly<IUser> | null = null;
    private readonly isInitialSelect: boolean;
    private readonly workflow: boolean;
    private readonly redirectTo: string;
    private readonly startingWorkday: boolean;
    private readonly vehicleHint: string | undefined;
    private suggestedVehicles: SuggestedVehicles | undefined;
    private buttonsBar: ButtonsBar | undefined;
    private assetsChecklistView: ListView | null;
    private assetSpinner: Spinner | null;
    private assetList: HTMLElement;
    private retryButton: HTMLElement;
    private assetSearch: HTMLInputElement;
    private searchTimeout: number;
    private searchVehiclePromise: Promise;
    private newDeviceId: string | null;

    constructor(options) {
        super(options, {
            title: "Select Vehicle",
            activeSection: "assets",
            pageLevel: options.pageLevel || PageLevel.TWO
        });

        let state = options.state;
        let currentVehicleId = getCurrentVehicle();

        this.api = this.app.api;
        this.sync = this.app.sync;
        this.assetSpinner = new Spinner();
        this.user = this.app.userService.getCurrentUser();
        this.deviceCache = makeDeviceCache(this.app.store);
        this.assetsChecklistView = null;
        this.workflow = options.workflow || !(currentVehicleId && currentVehicleId.id);
        this.isInitialSelect = !(options.changeVehicle || false) || (state && state.init && state.init === true);
        this.redirectTo = state && state.next ? state.next : getHomeHash();
        this.suggestedVehicles = new SuggestedVehicles({ app: this.app, isInitial: this.isInitialSelect, assetSelectedCallback: asset => this.assetSelected(asset) });
        this.newDeviceId = options.newDeviceId || null;
        this.startingWorkday = !!this.user && this.app.store.getState().data.workday[this.user.id] === WorkdayType.Stopped;
        this.vehicleHint = options.vehicleHint;
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #search-asset-selection-retrybutton": this.retry,
            "keyup #driver-asset-selection-input": this.searchForAsset,
            "submit #select-asset-form": this.searchForAsset
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        this.display(this.template({}))
            .then(() => {
                this.assetList = document.getElementById("driver-asset-selection-list") as HTMLElement;
                this.retryButton = document.getElementById("search-asset-selection-retrybutton") as HTMLElement;
                this.assetSearch = document.getElementById("driver-asset-selection-input") as HTMLInputElement;

                if (this.assetSearch) {
                    this.assetSearch.disabled = isIoxUsbDeviceAttached(this.app.store);
                }

                if (this.vehicleHint) {
                    this.assetSearch.value = this.vehicleHint;
                }

                if (this.newDeviceId) {
                    this.selectNewDevice();
                } else {
                    this.showSearchForm();
                    this.searchForVehicles(this.assetSearch ? this.assetSearch.value : "");
                }
            })
            .catch(handleApi);
        return this;
    }

    showNoVehicleButton() {
        this.buttonsBar && this.buttonsBar.hide();
        this.buttonsBar = new ButtonsBar(
            [
                {
                    title: this.getString("Continue without vehicle"),
                    id: `driver-asset-selection-no-vehicle-no-last-asset`,
                    clickHandler: () => { this.selectNoVehicle(); }
                }
            ],
            {
                isNightMode: this.app.store.getState().data.nightMode,
                showAnimation: true
            }
        );

        this.buttonsBar.show();
    }

    showSearchForm() {
        let searchInput = document.getElementById("select-asset-form");
        searchInput && show(searchInput);
        this.showNoVehicleButton();
        let findVehicleTitle = document.getElementById("find-asset-panel-title");
        findVehicleTitle && show(findVehicleTitle);
    }

    hideSearchForm() {
        let assetPanel = document.getElementById("select-asset-form");
        assetPanel && hide(assetPanel);
        this.buttonsBar && this.buttonsBar.hide();
        let findVehicleTitle = document.getElementById("find-asset-panel-title");
        findVehicleTitle && hide(findVehicleTitle);
    }

    constructAssetList(assets: IVehicleListItem[]) {
        let currentVehicle = getCurrentVehicle();
        let currentVehicleId = currentVehicle && currentVehicle.id;
        let checklistAssets = assets.map(asset => {
            return {
                id: asset.device.id,
                title: asset.name,
                subtitle: asset.subtitle,
                asset: asset.device,
                selected: currentVehicleId && asset.device.id && asset.device.id === currentVehicleId
            };
        });

        this.assetsChecklistView && this.assetsChecklistView.remove();
        this.assetsChecklistView = generateChecklist({
            items: checklistAssets,
            isCheckable: true,
            labelledBy: "title-text",
            selectCallback: item => this.assetSelected(item.get("asset")),
            context: this,
            el: this.assetList,
            translator: this.translator,
            noTranslate: true
        });
    }

    clearAssetsList() {
        empty(this.assetList);
    }

    retry() {
        let searchInput = this.assetSearch.value || "";
        this.searchForVehicles(searchInput, false);
    }

    searchForAsset() {
        this.searchTimeout && clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            this.searchForVehicles(this.assetSearch.value || "");
        }, KEYUP_TIMEOUT_OPTIMIZED_MS);
    }

    confirmDriverChange(device, user) {
        return new Promise<boolean>((resolve) => {
            let confirmDialog: Dialog = new Dialog(
                "confirm-assign-dialog",
                `<strong>Vehicle</strong><span class='noTranslate'>: ${ htmlEscape(device.name) }</span><br>
                <strong>Current driver</strong><span class='noTranslate'>: </span>${ user && user.name ? (`<span class='noTranslate'>${ htmlEscape(user.name) }</span>`) : "Unknown driver" }<br>
                You will replace the current driver assigned to this vehicle. Continue?`,
                this.translator,
                "Confirmation Required",
                [[
                    {
                        id: "cancel-assigning",
                        text: "No",
                        className: "button_secondary",
                        callback: () => {
                            confirmDialog.remove();
                            resolve(false);
                        }
                    },
                    {
                        id: "confirm-assigning",
                        text: "Yes",
                        callback: () => {
                            confirmDialog.remove();
                            resolve(true);
                        }
                    }
                ]],
                {
                    closable: false,
                    isNightMode: this.app.store.getState().data.nightMode
                }
            );

            confirmDialog.open().catch(handleApi);
        });
    }

    getLastDriver(deviceId: string) {
        let apiUser = this.api.getUser();
        let canGetUsers = this.app.securityService.can("getUsers", apiUser && apiUser.id ? apiUser.id : undefined);
        return getLastDriverForDevice(this.api, deviceId, canGetUsers);
    }

    assetSelected(asset) {
        this.selectDevice(asset);
    }

    selectNoVehicle() {
        if (!this.canStartVehicleSelection()) {
            return;
        }

        logger.log(logger.Source.ENTITY, "Attempting to select no vehicle", this.app.userService.getCurrentUser());

        this.startProgress(this.getString("Please wait"));
        this.cancelPromises();

        let oldVehicle = getCurrentVehicle();
        let noVehicleDevice = {
            "id": "NoDeviceId",
            "name": ""
        };
        let currentDriver = this.app.userService.getDriversSeatUser();
        let serverDate = getServerDate();
        let coDriver: IIdEntity | null = null;

        if (currentDriver && this.user && this.user.id && currentDriver.id && currentDriver.id !== this.user.id) {
            coDriver = { id: currentDriver.id };
        }

        let selectNoVehicleProfile = PerformanceProfilerFactory.getInstance(MeasureName.SelectNoVehicle);
        selectNoVehicleProfile.start();

        this.getCodriversAccessToDevice(noVehicleDevice.id)
            .then(coDriversWithoutCreds => {
                if (coDriversWithoutCreds.length > 0) {
                    return this.showLostSessionDialog(coDriversWithoutCreds);
                }

                return Promise.resolve();
            })
            .then(() => !oldVehicle && this.user ? getDeviceAssignedToDriver(this.api, this.user.id) : Promise.resolve(null))
            .then((currentlyAssignedVehicle: IIdEntity | null) => {
                if (!this.canStartVehicleSelection()) {
                    throw new Error("Vehicle selecting is not allowed.");
                }
                // When already on NoDeviceId, the driver is already tracked there; skip creating a DriverChange-to-Unknown.
                // This occurs after displacement when vehicleEvents.ts set NoDeviceId before navigating to the workflow.
                if (oldVehicle?.id === "NoDeviceId") {
                    this.handleVehicleChangeWithExemptionLog(noVehicleDevice as Device, serverDate, oldVehicle);
                    return Promise.resolve();
                }
                return this.app.vehicleService.setDriverVehicle(currentlyAssignedVehicle || oldVehicle, "UnknownDriverId", true, coDriver, serverDate).then(() => {
                    this.handleVehicleChangeWithExemptionLog(noVehicleDevice as Device, serverDate, oldVehicle);
                    return Promise.resolve();
                });
            })
            .then(() => {
                this.startProgress(this.getString("Synchronizing HOS"));
                clearTempAssetsFromState(this.app.store, this.app.userService);
                setCurrentVehicle({ "id": "NoDeviceId" });
                this.app.store.dispatch(OpenCabWorkflowActions.setSkippedAssetSelection(false));
                this.app.store.dispatch(DataActions.removeActiveEvent(ActiveEventType.VehicleRemoved));
                this.deviceCache.add([noVehicleDevice]);

                logger.log(logger.Source.ENTITY, "Did not select vehicle", this.app.userService.getCurrentUser());
                return this.app.dutyStatusLogService.hasUserLogs()
                        ? Promise.resolve()
                        : this.sync.synchronize(false, undefined, [SyncType.HOS]);
            })
            .then(() => this.app.manager.onVehicleSelected(this.app.context, noVehicleDevice, oldVehicle, true))
            .then(() => {
                dispatcher.trigger(DispatcherEvents.VehicleSelected, noVehicleDevice, oldVehicle);
                const finish = () => {
                    selectNoVehicleProfile.end();
                    dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep, false);
                };
                // Run the full sync in the background when selecting no vehicle
                // The HOS logs were already synced above
                deferUntilIdle(() => this.sync.synchronize(), { timeout: 1000, isVerboseLoggingEnabled: isVerboseLogging() });
                finish();
                return;
            })
            .catch(error => {
                selectNoVehicleProfile.end();
                this.handleSelectingError(error);
            });
    }

    handleSelectingError(error) {
        if (error.message.indexOf("Vehicle selecting is not allowed.") === -1) {
            handleApi(error);
        }

        this.stopProgress();
    }

    selectDevice(device) {
        if (!this.canStartVehicleSelection()) {
            return;
        }

        logger.log(logger.Source.ENTITY, `Attempting to select device '${ (htmlEscape(device.name) || device.id) }'`, this.app.userService.getCurrentUser());
        this.startProgress(this.getString("Selecting vehicle..."));
        this.cancelPromises();

        const selectedVehicleTimer = logger.timer(logger.Source.USER, "Selected vehicle tasks", this.user);
        this.getLastDriver(device.id)
            .then(driver => {
                if (needsDriverChangeConfirmation(driver, this.user?.id)) {
                    return isIoxUsbDeviceAttached(this.app.store) || this.confirmDriverChange(device, driver);
                } else {
                    return true;
                }
            })
            .then((isSelectingVehicleAllowed: boolean) => {
                if (!isSelectingVehicleAllowed) {
                    if (this.newDeviceId) {
                        this.newDeviceId = null;
                        this.render();
                    }

                    throw new Error("Vehicle selecting is not allowed.");
                } else {
                    return this.getCodriversAccessToDevice(device.id);
                }
            })
            .then((driversWithoutAccess: IIdEntity[]) => {
                if (driversWithoutAccess.length === 0) {
                    if (!this.canStartVehicleSelection()) {
                        throw new Error("Vehicle selecting is not allowed.");
                    }
                    this.newDeviceId = null;
                    let oldVehicle = getCurrentVehicle();
                    let currentDriver = this.app.userService.getDriversSeatUser();
                    let coDriver: any = null;

                    // If this user is not the driver, add an unknowndriverid driverchange for the current driver/vehicle
                    if (oldVehicle && !isNoVehicle() && currentDriver && this.user && this.user.id && currentDriver.id && currentDriver.id !== this.user.id) {
                        coDriver = { id: currentDriver.id };
                        return this.app.vehicleService.setDriverVehicle(oldVehicle, "UnknownDriverId", true, coDriver).then(() => this.setVehicleTask(device, oldVehicle));
                    } else {
                        return this.setVehicleTask(device, oldVehicle);
                    }
                }
                this.showNoAccessToDeviceDialog(driversWithoutAccess).catch(handleApi);
                return undefined;
            })
            .then(() => {
                selectedVehicleTimer(true);
            })
            .catch(error => {
                selectedVehicleTimer(false);
                if (this.app.store.getState().mobile.online !== NetworkStates.ONLINE && this.startingWorkday) {
                    const failToSelectVehicleTimer = logger.timer(logger.Source.USER, "Undo selected vehicle due to device offline", this.user);
                    const revertSelectVehicleTask = this.setVehicleTask(device, null);
                    revertSelectVehicleTask
                        ? revertSelectVehicleTask.then(() => failToSelectVehicleTimer(true))
                            .catch(rollbackErr => {
                                failToSelectVehicleTimer(false);
                                this.handleSelectingError(rollbackErr);
                            })
                        : failToSelectVehicleTimer(false);
                }
                this.handleSelectingError(error);
            });
    }

    selectNewDevice() {
        if (!this.canStartVehicleSelection()) {
            return;
        }

        let deviceId = this.newDeviceId;

        if (deviceId === "NoDeviceId") {
            this.newDeviceId = null;
            this.selectNoVehicle();
        } else {
            const errorCallback = (errorMessage?: string) => {
                errorMessage && toastr.error(
                    this.getString(errorMessage),
                    undefined,
                    undefined,
                    {
                        isUserNightMode: this.app.store.getState().data.nightMode
                    }
                );
                this.app.store.dispatch(DataActions.setCurrentHardwareId(undefined));
                this.newDeviceId = null;
                this.render();
            };
            this.api.call(
                "Get",
                {
                    typeName: "Device",
                    search: { id: deviceId }
                },
                result => {
                    if (!this.canStartVehicleSelection()) {
                        return;
                    }
                    const device = result[0];
                    if (device && (!device.activeTo || new Date(device.activeTo).getTime() >= getServerDate().getTime())) {
                        this.selectDevice(new Device(device));
                        toastr.success(this.getString("You have been assigned to") + ` ${ device.name }`);
                    } else {
                        errorCallback();
                    }
                },
                error => {
                    // Clear currentHardwareId so the page is able to load properly if the vehicle can't be assigned
                    handleApi(error);
                    errorCallback("Device could not be attached due to network issues.");
                }
            );
        }
    }

    setVehicleTask(newDevice: Device, oldVehicle: IIdEntity | null) {
        if (!this.canStartVehicleSelection()) {
            return;
        }

        let currentUser = this.app.userService.getCurrentUser();
        if (!currentUser) {
            logger.error(logger.Source.ERROR, "selectVehicle: Unable to select vehicle due to missing currentUser");
            return undefined;
        }

        let selectVehicleProfile = PerformanceProfilerFactory.getInstance(MeasureName.SelectVehicle);
        selectVehicleProfile.start();

        // set new vehicle for user
        setCurrentVehicle({ "id": newDevice.id });
        this.app.store.dispatch(OpenCabWorkflowActions.setSkippedAssetSelection(false));
        this.app.store.dispatch(DataActions.removeActiveEvent(ActiveEventType.VehicleRemoved));
        this.deviceCache.add(newDevice.toJSON());

        logger.log(logger.Source.ENTITY, `Selected vehicle '${ htmlEscape(newDevice.name) || newDevice.id }'`, currentUser);

        // Log user's location vs vehicle's location
        getDeviceStatusInfo(this.api, newDevice.id).then(dsi => {
            if (dsi && dsi.length === 1) {
                getLastPosition(this.app.store, {}, 10000)
                .then(position => {
                    if (position && position.coords) {
                        logger.log(
                            logger.Source.USER_EVENT,
                            `Current Geolocation: ${ position.coords.latitude }, ${ position.coords.longitude },
                            Device Location (via DSI): ${ dsi[0].latitude }, ${ dsi[0].longitude },
                            Distance From Vehicle: ${ new LatLon(dsi[0].latitude, dsi[0].longitude).distanceTo(new LatLon(position.coords.latitude, position.coords.longitude)) }km`
                        );
                    }
                })
                .catch(() => {
                    logger.error(logger.Source.ERROR, "checkPhoneGps: Failed to get location");
                });
            }
        }).catch(error => logger.error(logger.Source.ERROR, "checkDeviceStatusInfo: Failed to get DeviceStatusInfo", this.api.getUser(), error));

        let serverTime = getServerDate();
        const operationId = idSerializer.get();
        this.app.store.dispatch(DataActions.addActiveEvent({
            [ActiveEventType.VehicleSelected]: {
                operationId,
                deviceId: newDevice.id, dateTime: serverTime.toISOString(), userId: currentUser.id,
                pendingUserIds: this.app.userService.getUsers(true).map(user => user.id)
            }
        }));
        return this.app.vehicleService.setDriverVehicle(newDevice.toJSON(), undefined, undefined, null, serverTime)
            .then(() => {
                // Need to sync for all data because a vehicle affects all areas
                this.startProgress(this.getString("Synchronizing Asset Inspection logs, attached trailers, HOS, shipments, and messages..."));

                // make this user the driver. method will check if they're already the driver to avoid duplicate driver changes
                this.app.userService.setDriversSeatUser((currentUser as IUser).name, true);
                return this.app.dutyStatusLogService.hasUserLogs()
                    ? Promise.resolve()
                    : this.sync.synchronize(false, undefined, [SyncType.HOS]);
            })
            .then(() => this.app.manager.onVehicleSelected(this.app.context, newDevice.toJSON(), oldVehicle, true, serverTime))
            .then(() => {
                if (this.app.store.getState().data.activeEvents?.[ActiveEventType.VehicleSelected]?.operationId === operationId) {
                    this.app.store.dispatch(DataActions.removeActiveEvent(ActiveEventType.VehicleSelected));
                }
                // Triggers event after vehicle is set, but before we sync
                dispatcher.trigger(DispatcherEvents.VehicleSelected, newDevice.toJSON(), oldVehicle);
                // Run only the syncs needed for vehicle selection
                return this.runPrioritizedSync(VEHICLE_SELECTED_SYNC_TYPES);
            })
            .then(() => {
                let finish = () => {
                    selectVehicleProfile.end();

                    if (!this.workflow) {
                        this.app.router.navigate(this.redirectTo, true);
                    } else {
                        dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep, true);
                    }
                };

                deferUntilIdle(() => this.app.network.checkBridge(), { timeout: 1000, isVerboseLoggingEnabled: isVerboseLogging() });

                // remove any duplicate trailers from temp if driver switches vehicles after adding
                let trailerAttachmentsCollection = new TrailerAttachments(this.app.api, this.app.store);
                let activeCollection = trailerAttachmentsCollection.getCurrent();
                let tempAttachments = getAttachmentsFromState(this.app.store, this.app.userService);
                let uniqueNewAttachments: TrailerAttachment[] = tempAttachments
                    .filter(newAttachment => !activeCollection.some(existingAttachment => existingAttachment.trailer?.id === newAttachment.trailer?.id));
                if (uniqueNewAttachments.length !== tempAttachments.length) {
                    setAttachmentsInState(this.app.store, uniqueNewAttachments, this.app.userService);
                }

                let isHOSAllowed = newDevice.deviceFlags && newDevice.deviceFlags.isHOSAllowed;
                let isAutoHOSUndecided = newDevice.autoHos === undefined || newDevice.autoHos === "AUTO";
                let jurisdiction = this.app.store.getState().data.ELDJurisdiction;
                // Dispatch event if last status log is from same driver and same vehicle
                // and the status is PC, PC_Exempted or YM
                if (this.handleVehicleChangeWithExemptionLog(newDevice, serverTime, oldVehicle)) {
                    finish();
                } else if (isHOSAllowed && isAutoHOSUndecided && jurisdiction !== "EWD") {
                    const lastStatusLog = this.getLastLogForDriver(currentUser as IIdEntity);

                    let newLog = new DutyStatusLog({
                        id: "",
                        dateTime: getServerDate().toISOString(),
                        status: (lastStatusLog && lastStatusLog.status) || "OFF",
                        device: { id: newDevice.id },
                        driver: { id: (currentUser as IUser).id },
                        coDrivers: this.app.userService.getCoDrivers((currentUser as IUser).name),
                        origin: (lastStatusLog && lastStatusLog.origin) || "Manual"
                    });

                    this.sync.dutyStatusLogs.addItem(newLog, true)
                            .then(finish)
                            .catch(() => {
                                this.stopProgress();
                            });
                } else {
                    finish();
                }
            });
    }

    startProgress(workingMessage, isSelectedVehicle = true) {
        this.stopProgress(undefined, isSelectedVehicle);

        this.retryButton && hide(this.retryButton);
        if (isSelectedVehicle) {
            this.buttonsBar && this.buttonsBar.disableButtons();
            this.hideSearchForm();
        }

        // assetPanel won't be shown in IoxUsb-Auto-Selection mode, we need to show it manually.
        if (workingMessage) {
            let assetPanel = document.getElementById("select-asset-panel") as HTMLElement;
            assetPanel && (!isVisible(assetPanel)) && show(assetPanel);
        }

        this.assetList && hide(this.assetList);

        let selectionProgress = document.getElementById("driver-asset-selection-progress");
        selectionProgress && show(selectionProgress);

        let searchTextElement = document.getElementById("driver-asset-selection-search-text");
        searchTextElement && (searchTextElement.innerHTML = workingMessage);

        let searchSpinner = document.getElementById("driver-asset-selection-search-spinner");
        if (searchSpinner) {
            show(searchSpinner);
            this.assetSpinner?.spin(searchSpinner);
        }
    }

    stopProgress(workingMessage?: string, isSelectedVehicle = true) {
        this.assetSpinner?.stop();

        let progressElement = document.getElementById("driver-asset-selection-progress");
        if (progressElement) {
            if (workingMessage) {
                let searchTextElement = document.getElementById("driver-asset-selection-search-text");
                searchTextElement && (searchTextElement.innerHTML = workingMessage);

                show(progressElement);
            } else {
                hide(progressElement);
            }
            hide(document.getElementById("driver-asset-selection-search-spinner") as HTMLElement);

            this.assetList && show(this.assetList);
        }

        if (isSelectedVehicle) {
            this.buttonsBar && this.buttonsBar.enableButtons();
            this.showSearchForm();
        }
    }

    remove() {
        this.cancelPromises();
        delete this.suggestedVehicles;
        this.assetsChecklistView && this.assetsChecklistView.remove();
        this.searchTimeout && clearTimeout(this.searchTimeout);

        if (this.assetSpinner) {
            this.assetSpinner.stop();
            this.assetSpinner = null;
        }
        this.buttonsBar && this.buttonsBar.hide();
        delete this.buttonsBar;

        super.remove();
        return this;
    }

    private canStartVehicleSelection(): boolean {
        const pending = this.app.store.getState().data.activeEvents?.[ActiveEventType.VehicleSelected];
        if (pending?.pendingUserIds && pending.pendingUserIds.indexOf(pending.userId) === -1) {
            this.app.store.dispatch(DataActions.removeActiveEvent(ActiveEventType.VehicleSelected));
            return true;
        }
        if (pending) {
            this.handleSelectingError(new Error("Vehicle selecting is not allowed."));
            toastr.error(this.getString("Please try again."));
            return false;
        }
        return true;
    }

    private cancelPromises() {
        this.searchVehiclePromise && this.searchVehiclePromise.cancel();
    }

    private searchForVehicles(searchInput: string, useCached = true) {
        let searchString = searchInput ? this.getString("Searching for '{0}'").replace("{0}", htmlEscape(searchInput)) : this.getString("Searching for recent or nearby vehicles...");
        this.startProgress(searchString, false);
        this.cancelPromises();

        const vehiclePromise = !searchInput && this.suggestedVehicles
            ? this.suggestedVehicles.findSuggestedVehicles(useCached)
            : new Promise(searchVehicles(this.api, searchInput));
        this.searchVehiclePromise = vehiclePromise
            .then(devices => {
                if (devices && devices.length) {
                    this.constructAssetList(devices);
                    this.stopProgress(undefined, false);

                    !searchInput && show(this.retryButton);
                } else {
                    this.clearAssetsList();
                    if (searchInput) {
                        this.stopProgress(this.getString("Could not find any vehicles matching your search terms."), false);
                        return;
                    }

                    show(this.retryButton);
                    if (this.app.store.getState().mobile.gpsConnected) {
                        this.stopProgress(`${ this.getString("Could not find any recent or nearby vehicles.") }`, false);
                    } else {
                        this.stopProgress(`${ this.getString("No nearby vehicles detected. Check your GPS settings or move to an open area for better signal.") }`, false);
                    }
                }
            })
            .catch(() => {
                this.clearAssetsList();
                this.stopProgress(this.getString("Please try again."), false);
                show(this.retryButton);
            });
    }

    private showNoAccessToDeviceDialog(codrivers) {
        let coDriversNames = codrivers.map(codriver => codriver.name);
        let solveSelectDeviceDialog = new Dialog(
            "select-device-conflict",
            `These co-drivers do not have access to the selected vehicle: ${ coDriversNames.map(name => `<div class="noTranslate codriver-without-rights">${ htmlEscape(name) }</div>`).join("") }`,
            this.translator,
            "Device selection warning",
            [
                {
                    id: "reselect-device-button",
                    text: "Select another device",
                    callback: () => {
                        solveSelectDeviceDialog.remove();
                        this.stopProgress();
                    }
                }
            ],
            {
                closable: false,
                showCloseIcon: false,
                isNightMode: this.app.store.getState().data.nightMode
            }
        );
        return solveSelectDeviceDialog.open();
    }

    private showLostSessionDialog(coDriversWithoutSession: any[]) {
        return new Promise(resolve => {
            let codriverSessionLostDialog = new Dialog(
                "codriver-session-lost",
                `These co-drivers lost their sessions: ${ coDriversWithoutSession.map(codr => `<div class="noTranslate codriver-without-rights">${ htmlEscape(codr.name) }</div>`).join("") }`,
                this.translator,
                "Device selection warning",
                [
                    {
                        id: "logout-codrivers-proceed",
                        text: "Logout these codrivers and proceed",
                        callback: () => {
                            dispatcher.trigger(DispatcherEvents.RemoveUserSessions, coDriversWithoutSession.map(driver => driver.name));
                            codriverSessionLostDialog.remove();
                            resolve();
                        }
                    }
                ],
                {
                    closable: false,
                    showCloseIcon: false,
                    isNightMode: this.app.store.getState().data.nightMode
                }
            );

            codriverSessionLostDialog.open().catch(handleApi);
        });
    }

    private getCodriversCredentials(coDrivers: any[]): Promise<any[]> {
        return new Promise((resolve) => {
            if (coDrivers.length !== 0) {
                let coDriversCredentials = coDrivers.map(codriver => (this.api.getDriverCredentials(codriver.name) as ICredentialsWithServer));
                let lostSessionDrivers = coDrivers.filter((_1, index) => !coDriversCredentials[index]);

                if (lostSessionDrivers.length) {
                    this.showLostSessionDialog(lostSessionDrivers)
                        .then(() => {
                            let filteredCredentials = coDriversCredentials.filter(echo);
                            resolve(filteredCredentials);
                        })
                        .catch(handleApi);
                } else {
                    resolve(coDriversCredentials);
                }
            } else {
                resolve(coDrivers);
            }
        });
    }

    private getCodriversAccessToDevice(deviceId: string) {
        let driverName = this.user ? this.user.name : undefined;
        let coDrivers = this.app.userService.getCoDrivers(driverName, true);

        return this.getCodriversCredentials(coDrivers)
            .then((creds => {
                // refreshing coDrivers list if some of them were deleted after getting credentials
                coDrivers = this.app.userService.getCoDrivers(driverName, true);
                return creds.map((cred) => {
                    const coDriverContext = this.app.manager.getCachedContext(cred.credentials.userName);
                    if (coDriverContext) {
                        return new Promise((res, rej) => {
                            coDriverContext.api.call("Get", {
                                typeName: "Device",
                                search: { id: deviceId }
                            }, res, rej);
                        });
                    } else {
                        logger.error(logger.Source.ERROR, `Can't get driver (userName: ${ cred.credentials.userName }) context to determine access to device.`, cred.credentials.userName);
                        return Promise.resolve([]);
                    }
                });
            }))
            .then(devicePromises => Promise.all(devicePromises))
            .then(deviceResponses => coDrivers.filter((_1, index) => !deviceResponses[index][0]));
    }

    private getLastLogForDriver(driver: IIdEntity) {
        let driverLogs = this.app.dutyStatusLogService.getHOSLogs(true, driver.id).filter(hosLog => hosLog.isStandardStatusDriverLog());
        return this.app.dutyStatusLogService.getLatestLog(driverLogs);
    }

    private handleVehicleChangeWithExemptionLog(newDevice: Device, serverTime: Date, oldVehicle: IIdEntity | null): boolean {
        const activeUsers = this.app.userService.getUsers(true);
        const fromNoVehicle = (!oldVehicle || oldVehicle.id === "NoDeviceId") && !window.DRIVE_DEBUG?.KEEP_EXEMPTION_ON_LOGIN;
        let isDispatched = false;
        for (let i = 0; i < activeUsers.length; i++) {
            const latestStatusLog = this.getLastLogForDriver(activeUsers[i]);
            if (latestStatusLog && (latestStatusLog.device.id !== newDevice.id || fromNoVehicle) && (latestStatusLog.isPcYm())) {
                dispatcher.trigger(DispatcherEvents.VehicleChangedInExemption, latestStatusLog, newDevice, serverTime);
                isDispatched = true;
            }
        }

        return isDispatched;
    }

    /**
     * Runs a sync, deferring the main sync if selectiveSync is provided
     * @param prioritizedSelectiveSyncs Array of SyncTypes to prioritize
     * @returns Promise that resolves when the prioritized syncs complete
     */
    private runPrioritizedSync(prioritizedSelectiveSyncs?: SyncType[]) {
        if (prioritizedSelectiveSyncs?.length && prioritizedSelectiveSyncs?.length > 0) {
            // Run only the syncs needed for vehicle selection, deferring the main sync until later
            // Find subset of syncs that are part of syncTypesForWorkflow but not in prioritizedSelectiveSyncs
            const remainingSyncs = syncTypesForWorkflow?.filter(syncType => prioritizedSelectiveSyncs.indexOf(syncType) === -1) || [];
            const runLowerPrioritySyncInBackground = () => {
                if (this.isInitialSelect) {
                    // Run only the subset of syncs that are part of syncTypesForWorkflow but not in prioritizedSelectiveSyncs
                    return this.sync.synchronize(false, undefined, remainingSyncs);
                }

                // Otherwise, if we're selecting a vehicle in the app (not as part of the workflow) run all the remaining sync types deferred
                // as the rest of the workflow is essentially bypassed
                return this.sync.synchronize(false, undefined, prioritizedSelectiveSyncs, undefined, true);
            };
            deferUntilIdle(() => runLowerPrioritySyncInBackground(), { timeout: 1000, isVerboseLoggingEnabled: isVerboseLogging() });
            return this.sync.synchronize(false, undefined, prioritizedSelectiveSyncs);
        } else {
            return this.sync.synchronize();
        }
    }

}